// src/Datenschutz.js — AVS Cloud | DSGVO-konform | Stand: Juni 2026
import React, { useEffect } from 'react';
import { setPageMeta } from './utils/seo';

const s = {
  wrap: { minHeight: '100vh', padding: '2rem clamp(1rem, 4vw, 2.5rem) 5rem', position: 'relative', zIndex: 1 },
  inner: { maxWidth: '760px', margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', textDecoration: 'none', marginBottom: '2.5rem', cursor: 'pointer' },
  title: { fontFamily: 'var(--font-head)', fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 900, letterSpacing: '-0.04em', color: 'var(--white)', marginBottom: '0.4rem', lineHeight: 1.1 },
  subtitle: { fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '2.5rem', letterSpacing: '0.04em', paddingBottom: '2rem', borderBottom: '1px solid var(--border)' },
  block: { padding: '1.75rem 0', borderBottom: '1px solid var(--border)' },
  blockLast: { padding: '1.75rem 0' },
  heading: { fontSize: '0.95rem', fontWeight: 700, color: 'var(--cyan)', letterSpacing: '-0.01em', marginBottom: '0.85rem', fontFamily: 'var(--font-head)' },
  subheading: { fontSize: '0.85rem', fontWeight: 700, color: 'var(--white)', marginTop: '1rem', marginBottom: '0.5rem' },
  p: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, margin: 0 },
  pmt: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, marginTop: '0.75rem' },
  ul: { margin: '0.75rem 0 0 1.2rem', padding: 0 },
  li: { fontSize: '0.88rem', color: 'var(--muted)', lineHeight: 1.8, paddingLeft: '0.3rem' },
  link: { color: 'var(--cyan)', textDecoration: 'underline', textUnderlineOffset: '3px' },
  strong: { color: 'var(--white)', fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '0.75rem', fontSize: '0.82rem' },
  th: { textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--cyan)', fontWeight: 600, borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' },
  td: { padding: '0.5rem 0.75rem', color: 'var(--muted)', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'top', lineHeight: 1.6 },
  tdKey: { padding: '0.5rem 0.75rem', color: 'var(--white)', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'top', lineHeight: 1.6 },
};

export default function Datenschutz() {
  useEffect(() => {
    setPageMeta({
      title: 'Datenschutzerklärung | AVS Cloud',
      description: 'Datenschutzerklärung von AVS Cloud gemäß DSGVO. Informationen zur Verarbeitung personenbezogener Daten durch AquaV Services, Hamburg.',
      path: '/datenschutz',
    });
  }, []);
  return (
    <div style={s.wrap}>
      <div style={s.inner}>
        <a href="/" style={s.back}>← Zurück zur Cloud</a>
        <h1 style={s.title}>Datenschutzerklärung</h1>
        <p style={s.subtitle}>Stand: Juni 2026</p>

        {/* §1 Verantwortlicher */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 1 Verantwortlicher</h2>
          <p style={s.p}>Verantwortlicher im Sinne der DSGVO ist:</p>
          <p style={s.pmt}>
            Mert Munz<br />
            c/o IP-Management #10553<br />
            Ludwig-Erhard-Str. 18<br />
            20459 Hamburg<br />
            Deutschland<br />
            E-Mail:{' '}
            <a href="mailto:aquavservices@gmail.com" style={s.link}>
              aquavservices@gmail.com
            </a>
          </p>
        </div>

        {/* §2 Hosting & Server-Logfiles */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 2 Hosting & Server-Logfiles</h2>
          <p style={s.p}>
            AVS Cloud wird auf einem Server in einem Rechenzentrum in den{' '}
            <span style={s.strong}>Niederlanden (EU-Mitgliedstaat)</span> gehostet.
            Es findet kein Transfer personenbezogener Daten in Drittländer statt.
            Rechtsgrundlage für die Übermittlung innerhalb der EU: Art. 45 DSGVO.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Auftragsverarbeiter (Art. 28 DSGVO):</span> Es
            besteht ein Auftragsverarbeitungsvertrag nach Art. 28 DSGVO mit dem
            Hosting-Anbieter.
          </p>
          <p style={s.pmt}>
            Beim Aufruf werden folgende Daten automatisch in Server-Logfiles
            gespeichert:
          </p>
          <ul style={s.ul}>
            {[
              'IP-Adresse des anfragenden Geräts',
              'Datum und Uhrzeit des Zugriffs',
              'Aufgerufene URL',
              'HTTP-Statuscode',
              'Übertragene Datenmenge',
              'Browser-Typ und Betriebssystem (User-Agent)',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
            an Sicherheit und Betrieb). Speicherdauer: max.{' '}
            <span style={s.strong}>7 Tage</span>, danach automatische Löschung.
          </p>
        </div>

        {/* §3 Registrierung & Nutzerkonto */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 3 Registrierung & Nutzerkonto</h2>
          <p style={s.p}>
            Die Anmeldung erfolgt ausschließlich über{' '}
            <span style={s.strong}>Discord OAuth2</span>. Folgende Daten werden
            gespeichert:
          </p>
          <ul style={s.ul}>
            {[
              'Discord-Nutzer-ID (eindeutiger Identifier)',
              'Benutzername',
              'Profilbild (Avatar-URL)',
              'E-Mail-Adresse (sofern von Discord bereitgestellt)',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Intern zusätzlich gespeichert: verwendeter Speicherplatz,
            Speicherlimit, Account-Status, Rolle (Nutzer/Admin), Theme-Präferenz,
            letzter Login.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Rechnungsadresse:</span> Vor der ersten
            kostenpflichtigen Buchung ist die Angabe einer Rechnungsadresse
            (Vor- und Nachname, Straße + Hausnummer, PLZ, Ort, Land) erforderlich.
            Rechtsgrundlage: Art. 6 Abs. 1 lit. c DSGVO i.V.m. § 15 UStG.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage für Nutzerkonto: Art. 6 Abs. 1 lit. b DSGVO. Es gilt
            die{' '}
            <a
              href="https://discord.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              Datenschutzerklärung von Discord
            </a>
            .
          </p>
        </div>

        {/* §4 Datei-Upload & Metadaten */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 4 Datei-Upload & gespeicherte Metadaten</h2>
          <p style={s.p}>
            Alle Dateien werden mit{' '}
            <span style={s.strong}>AES-256-GCM</span> verschlüsselt gespeichert.
            Pro Datei werden folgende Daten erfasst:
          </p>

          <p style={{ ...s.p, ...s.subheading, marginTop: '1rem' }}>
            Basis-Metadaten (alle Dateitypen)
          </p>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Feld</th>
                <th style={s.th}>Beschreibung</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Datei-ID', 'Eindeutige UUID zur Identifikation'],
                ['Dateiname', 'Originaler Dateiname (bereinigt)'],
                ['Dateigröße', 'Größe in Bytes'],
                ['MIME-Typ', 'Dateityp (z.B. image/jpeg, application/pdf)'],
                ['Hochladedatum', 'Datum und Uhrzeit des Uploads'],
                ['Nutzer-Zuordnung', 'Discord-ID des Eigentümers'],
                [
                  'Content-Hash',
                  'SHA-256-Hash zur Duplikaterkennung im eigenen Account',
                ],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={s.tdKey}>{k}</td>
                  <td style={s.td}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ ...s.p, ...s.subheading, marginTop: '1.25rem' }}>
            EXIF-Metadaten (nur Bilder: JPEG, PNG, HEIC, TIFF, WebP)
          </p>
          <table style={{ ...s.table, marginTop: '0.75rem' }}>
            <thead>
              <tr>
                <th style={s.th}>Kategorie</th>
                <th style={s.th}>Gespeicherte Felder</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Kamera', 'Hersteller, Modell, verwendete Software'],
                [
                  'Aufnahmedatum',
                  'Datum und Uhrzeit der Aufnahme (DateTimeOriginal)',
                ],
                [
                  'GPS-Standort',
                  'Breitengrad, Längengrad, Höhe — nur wenn im Bild enthalten',
                ],
                [
                  'Technisch',
                  'Bildbreite, Bildhöhe, Ausrichtung, ISO, Blende, Belichtungszeit, Brennweite, Blitz',
                ],
              ].map(([k, v]) => (
                <tr key={k}>
                  <td style={s.tdKey}>{k}</td>
                  <td style={s.td}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Dateien werden bis zur
            Kontolöschung gespeichert.
          </p>
        </div>

        {/* §5 Duplikaterkennung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 5 Duplikaterkennung</h2>
          <p style={s.p}>
            Beim Upload wird ein SHA-256-Hash des Dateiinhalts berechnet. Dieser
            dient ausschließlich zur Erkennung von Duplikaten{' '}
            <span style={s.strong}>innerhalb deines eigenen Accounts</span>. Es
            findet kein Abgleich mit Dateien anderer Nutzer statt.
          </p>
        </div>

        {/* §6 Automatische Sicherheitsprüfung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 6 Automatische Sicherheitsprüfung (Virenscanner)</h2>
          <p style={s.p}>
            Beim Upload wird jede Datei automatisch auf Schadsoftware geprüft.
            Die Prüfung erfolgt lokal auf unserem Server mittels ClamAV
            (Open-Source-Virenscanner). Dabei wird der Dateiinhalt im
            Arbeitsspeicher analysiert und nicht an Dritte übermittelt.
          </p>
          <p style={s.pmt}>
            Bei unklarem Ergebnis (Verdachtsfall) wird der{' '}
            <span style={s.strong}>SHA-256-Hash</span> der Datei für eine
            Hash-Abfrage beim Dienst VirusTotal (Google Ireland Ltd.) verwendet.
            Es wird ausschließlich der Datei-Hash übermittelt — kein Dateiinhalt
            und keine personenbezogenen Daten verlassen unseren Server. VirusTotal
            gleicht den Hash gegen seine Datenbank ab und gibt zurück, ob der Hash
            als schädlich bekannt ist. Rechtsgrundlage für die Übermittlung des
            Hashs: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an
            Sicherheit des Dienstes; keine Rückführung auf personenbezogene Daten
            möglich).
          </p>
          <p style={s.pmt}>
            Ergebnisse der Prüfung (Dateiname, Dateigröße, Scan-Status,
            Zeitpunkt) werden protokolliert. Bei Schadsoftwarefund wird der
            Upload blockiert und der Betreiber benachrichtigt.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
            an der Sicherheit des Dienstes und Schutz aller Nutzer).
          </p>
        </div>

        {/* §8 Share-Links */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 7 Share-Links</h2>
          <p style={s.p}>
            Nutzer können Dateien über öffentliche Share-Links teilen. Beim Aufruf
            eines Share-Links werden IP-Adresse und Zugriffszeit in den
            Server-Logfiles erfasst (Art. 6 Abs. 1 lit. f DSGVO). Der Nutzer ist
            selbst verantwortlich für Inhalte, die er öffentlich teilt.
          </p>
        </div>

        {/* §8 Zahlungsabwicklung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 8 Zahlungsabwicklung</h2>
          <p style={s.p}>
            Für kostenpflichtige Leistungen wird die Zahlungsabwicklung durch
            externe Zahlungsdienstleister vorgenommen. Kartendaten und
            Zahlungsdaten werden{' '}
            <span style={s.strong}>nicht auf AVS-Servern gespeichert</span>.
          </p>

          <p style={s.subheading}>Stripe</p>
          <p style={s.p}>
            Anbieter: Stripe Payments Europe, Ltd., 1 Grand Canal Street Lower,
            Grand Canal Dock, Dublin, Irland<br />
            Auftragsverarbeiter nach Art. 28 DSGVO — Auftragsverarbeitungsvertrag
            abgeschlossen.<br />
            Verarbeitete Daten: Rechnungsadresse, Zahlungsmittelinformationen,
            E-Mail-Adresse, IP-Adresse, Transaktionsdaten<br />
            Zweck: Zahlungsabwicklung<br />
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO<br />
            Datenschutzerklärung:{' '}
            <a
              href="https://stripe.com/de/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              stripe.com/de/privacy
            </a>
          </p>

          <p style={s.subheading}>PayPal</p>
          <p style={s.p}>
            Anbieter: PayPal (Europe) S.à r.l. et Cie, S.C.A., 22-24 Boulevard
            Royal, 2449 Luxemburg<br />
            Auftragsverarbeiter nach Art. 28 DSGVO — Auftragsverarbeitungsvertrag
            abgeschlossen.<br />
            Verarbeitete Daten: PayPal-Konto-E-Mail, Rechnungsadresse,
            Transaktionsdaten<br />
            Zweck: Zahlungsabwicklung<br />
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO<br />
            Datenschutzerklärung:{' '}
            <a
              href="https://www.paypal.com/de/webapps/mpp/ua/privacy-full"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              paypal.com/de — Datenschutz
            </a>
          </p>
        </div>

        {/* §9 Einwilligungs-Protokollierung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 9 Einwilligungs-Protokollierung</h2>
          <p style={s.p}>
            Bei kostenpflichtigen Buchungen wird Ihre ausdrückliche Zustimmung zum
            vorzeitigen Beginn der Dienstleistung (§ 356 Abs. 5 BGB)
            protokolliert. Gespeichert werden: Zustimmungstext, Zeitstempel
            (ISO 8601), IP-Adresse, User-Agent.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. c DSGVO (gesetzliche
            Beweispflicht, Rechenschaftspflicht nach Art. 5 Abs. 2 DSGVO).
            Speicherdauer: 3 Jahre nach Ende der Geschäftsbeziehung.
          </p>
        </div>

        {/* §10 Technische und organisatorische Maßnahmen */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 10 Technische und organisatorische Maßnahmen (TOMs)</h2>
          <p style={s.p}>
            Wir setzen folgende Sicherheitsmaßnahmen ein:
          </p>
          <ul style={s.ul}>
            {[
              'AES-256-GCM Verschlüsselung aller gespeicherten Dateien',
              'HTTPS/TLS 1.2 und 1.3 für alle Datenübertragungen (TLS 1.0/1.1 deaktiviert)',
              'HSTS (HTTP Strict Transport Security, 2 Jahre, inkl. Subdomains)',
              'Sicherheits-Header: X-Content-Type-Options, X-Frame-Options (DENY), Referrer-Policy, Permissions-Policy',
              'JWT-Session ausschließlich als httpOnly-Cookie gespeichert (kein localStorage — kein Zugriff per JavaScript)',
              'Zugriffskontrolle über Discord OAuth2 und JWT-Tokens',
              'Session-Timeout nach 10 Minuten Inaktivität',
              'Rate-Limiting gegen Brute-Force-Angriffe',
              'Server-Zugang nur über SSH mit Key-Authentifizierung',
              'Regelmäßige Sicherheitsupdates',
              'Automatische Malware-Prüfung aller Uploads (ClamAV)',
              'Server in EU (Niederlande) — kein Drittlandtransfer',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
        </div>

        {/* §11 Rechnungen & Aufbewahrung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 11 Rechnungen & Aufbewahrung (§ 147 AO)</h2>
          <p style={s.p}>
            Rechnungen werden gemäß § 147 Abs. 1 Nr. 1 AO für{' '}
            <span style={s.strong}>10 Jahre</span> aufbewahrt. Bei Kontolöschung
            werden personenbezogene Daten in Rechnungen anonymisiert (Name ersetzt,
            Adresse gelöscht). Betrag, Zeitraum und Status bleiben für die
            gesetzliche Aufbewahrungspflicht erhalten.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. c DSGVO i.V.m. § 147 AO.
          </p>
        </div>

        {/* §12 Session & Authentifizierung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12 Session & Authentifizierung (JWT, httpOnly-Cookie)</h2>
          <p style={s.p}>
            Nach dem Login wird ein JWT-Token als{' '}
            <span style={s.strong}>httpOnly-Cookie</span> gesetzt (nicht über
            JavaScript auslesbar). Sessions laufen nach 24 Stunden automatisch ab.
            Es werden keine Tracking- oder Marketing-Cookies eingesetzt — daher ist
            kein Cookie-Consent-Banner erforderlich.
          </p>
          <p style={s.pmt}>Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.</p>
        </div>

        {/* §12a IP-Logging bei Login/Logout */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12a IP-Protokollierung bei Login und Logout</h2>
          <p style={s.p}>
            Bei jedem An- und Abmeldevorgang wird die IP-Adresse des Nutzers
            erfasst und in einem internen Sicherheitsprotokoll gespeichert.
          </p>
          <ul style={s.ul}>
            {[
              'IP-Adresse',
              'Zeitstempel (Datum und Uhrzeit)',
              'Ereignistyp (Login / Login-Fehler / Logout)',
              'Discord-Benutzername (bei erfolgreichem Login)',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            <span style={s.strong}>Zweck:</span> Erkennung unberechtigter Zugriffe,
            Nachvollziehbarkeit von Sicherheitsereignissen, Schutz des Kontos.
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Speicherdauer:</span> 90 Tage, danach automatische
            Löschung.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an
            der Sicherheit des Dienstes und Schutz aller Nutzer).
          </p>
        </div>

        {/* §12b VPN-Erkennung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12b VPN- und Proxy-Erkennung</h2>
          <p style={s.p}>
            Um den Missbrauch des Dienstes zu verhindern, wird bei jedem
            Anmeldeversuch die IP-Adresse des Nutzers über den externen Dienst{' '}
            <span style={s.strong}>proxycheck.io</span> (iiNetworks Ltd.,
            Großbritannien) abgefragt.
          </p>
          <p style={s.pmt}>
            Dabei wird lediglich die IP-Adresse an proxycheck.io übermittelt. Der
            Dienst gibt zurück, ob es sich um eine bekannte VPN-, Proxy- oder
            Rechenzentrumsadresse handelt. Es werden keine weiteren personenbezogenen
            Daten übermittelt.
          </p>
          <p style={s.pmt}>
            Wird ein VPN oder Proxy erkannt, wird der Login verweigert und die
            IP-Adresse sowie der Grund der Ablehnung intern protokolliert
            (Speicherdauer: 90 Tage).
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Drittland-Transfer:</span> proxycheck.io
            verarbeitet Anfragen in Großbritannien. Seit dem Brexit gilt
            Großbritannien als Drittland. Die Übermittlung erfolgt auf Grundlage
            des Angemessenheitsbeschlusses der EU-Kommission vom 28.06.2021
            (Art. 45 DSGVO).
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
            an der Sicherheit des Dienstes).
          </p>
        </div>

        {/* §12c Discord-Webhook-Logging */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12c Sicherheits-Ereignisprotokoll (Discord Webhook)</h2>
          <p style={s.p}>
            Sicherheitsrelevante Ereignisse (Login, Login-Fehler, Login-Blockierung
            durch VPN/Proxy, Logout, große Dateidownloads {'>'} 1 GB, Serverfehler)
            werden automatisch als Benachrichtigung an einen privaten{' '}
            <span style={s.strong}>Discord-Server des Betreibers</span> übertragen.
          </p>
          <p style={s.pmt}>
            Dabei werden folgende Daten übermittelt:
          </p>
          <ul style={s.ul}>
            {[
              'IP-Adresse',
              'Ereignistyp und Zeitstempel',
              'Discord-Benutzername (bei Login-Ereignissen)',
              'Dateiname und Dateigröße (nur bei Downloads > 1 GB)',
              'HTTP-Fehlercode und Route (nur bei Serverfehlern)',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Diese Daten werden ausschließlich vom Betreiber eingesehen und nicht
            an Dritte weitergegeben. Discord Inc. (San Francisco, USA) verarbeitet
            Nachrichten als Auftragsverarbeiter. Die Übermittlung in die USA erfolgt
            auf Grundlage von Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).
          </p>
          <p style={s.pmt}>
            <span style={s.strong}>Speicherdauer:</span> Discord speichert Nachrichten
            bis zur manuellen Löschung durch den Betreiber, längstens 90 Tage.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
            an der Sicherheit und dem Betrieb des Dienstes,
            Rechenschaftspflicht gem. Art. 5 Abs. 2 DSGVO).
          </p>
        </div>

        {/* §12d Team-Speicher */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12d Team-Speicher</h2>
          <p style={s.p}>
            Nutzer können Team-Speicher erstellen, andere Nutzer dazu einladen
            und gemeinsam Dateien speichern. Dabei werden folgende Daten
            verarbeitet:
          </p>
          <p style={s.pmt}>
            Für jeden Team-Speicher werden gespeichert: Name des Speichers,
            Inhaber-Zuordnung, Erstellungsdatum, genutzter und zugewiesener
            Speicherplatz sowie der Status des Speichers.
          </p>
          <p style={s.pmt}>
            Für jede Mitgliedschaft werden gespeichert: Zuordnung zum
            Team-Speicher, Nutzer-ID des Mitglieds, vergebene Berechtigung
            sowie das Datum des Beitritts.
          </p>
          <p style={s.pmt}>
            Für Einladungen werden gespeichert: Nutzer-ID von Einladendem und
            Eingeladenem, Berechtigung, Status der Einladung und Ablaufdatum.
          </p>
          <p style={s.pmt}>
            Bei Versand einer Einladung wird eine E-Mail an die im Nutzerkonto
            hinterlegte E-Mail-Adresse des eingeladenen Nutzers gesendet. Die
            E-Mail enthält den Benutzernamen des Einladenden und den Namen des
            Team-Speichers.
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Die Daten werden bis
            zur Löschung des Team-Speichers oder bis zur Kontolöschung des
            betreffenden Nutzers gespeichert.
          </p>
        </div>

        {/* §12e E-Mail-Versand */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 12e E-Mail-Versand</h2>
          <p style={s.p}>
            Für den Versand transaktionaler E-Mails (Widerrufsbestätigung,
            Rechnungsversand, Team-Einladungen) wird der E-Mail-Dienst von{' '}
            <span style={s.strong}>
              Google Ireland Limited, Gordon House, Barrow Street, Dublin 4,
              Irland
            </span>{' '}
            genutzt. Dabei wird die E-Mail-Adresse des Empfängers an die
            Server von Google übermittelt.
          </p>
          <p style={s.pmt}>
            Google Ireland Limited ist Auftragsverarbeiter nach Art. 28 DSGVO;
            ein Auftragsverarbeitungsvertrag ist abgeschlossen. Es findet kein
            Transfer in Drittländer statt, soweit die Verarbeitung über Google
            Ireland Limited erfolgt. Datenschutzerklärung:{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              policies.google.com/privacy
            </a>
          </p>
          <p style={s.pmt}>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
          </p>
        </div>

        {/* §13 Kontolöschung */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 13 Kontolöschung (Art. 17 DSGVO)</h2>
          <p style={s.p}>
            Nutzer können die Löschung ihres Kontos jederzeit über{' '}
            <span style={s.strong}>Einstellungen → Account löschen</span>{' '}
            beantragen. Nach Bestätigung werden{' '}
            <span style={s.strong}>sofort</span> gelöscht: alle Dateien,
            Share-Links, Nutzerkonto, alle Sessions, alle Team-Speicher
            (einschließlich enthaltener Dateien und Mitgliedschaften) sowie
            alle Team-Einladungen. Rechnungen werden anonymisiert und gemäß
            § 147 AO für 10 Jahre aufbewahrt. Widerrufsanträge
            (E-Mail-Adresse, IP-Adresse) und Missbrauchsmeldungen
            (Reporter-E-Mail, IP-Adresse) werden ebenfalls anonymisiert
            (Art. 17 DSGVO).
          </p>
        </div>

        {/* §14 Ihre Rechte */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 14 Ihre Rechte</h2>
          <p style={s.p}>Sie haben gemäß DSGVO folgende Rechte:</p>
          <ul style={s.ul}>
            {[
              'Auskunft (Art. 15 DSGVO) — über alle gespeicherten Daten inkl. EXIF-Metadaten',
              'Berichtigung (Art. 16 DSGVO) — unrichtiger Daten',
              'Löschung (Art. 17 DSGVO) — über die Einstellungen oder per E-Mail',
              'Einschränkung der Verarbeitung (Art. 18 DSGVO)',
              'Datenübertragbarkeit (Art. 20 DSGVO)',
              'Widerspruch (Art. 21 DSGVO) gegen die Verarbeitung',
            ].map((i) => (
              <li key={i} style={s.li}>{i}</li>
            ))}
          </ul>
          <p style={s.pmt}>
            Kontakt:{' '}
            <a href="mailto:aquavservices@gmail.com" style={s.link}>
              aquavservices@gmail.com
            </a>
          </p>
        </div>

        {/* §15 Beschwerderecht */}
        <div style={s.block}>
          <h2 style={s.heading}>§ 15 Beschwerderecht (Landesbeauftragter BW)</h2>
          <p style={s.p}>
            Der Landesbeauftragte für den Datenschutz und die Informationsfreiheit
            Baden-Württemberg<br />
            Lautenschlagerstraße 20, 70173 Stuttgart<br />
            <a
              href="https://www.baden-wuerttemberg.datenschutz.de"
              target="_blank"
              rel="noopener noreferrer"
              style={s.link}
            >
              www.baden-wuerttemberg.datenschutz.de
            </a>
          </p>
        </div>

        {/* §16 Aktualität */}
        <div style={s.blockLast}>
          <h2 style={s.heading}>§ 16 Aktualität</h2>
          <p style={s.p}>Stand: Juni 2026.</p>
        </div>
      </div>
    </div>
  );
}
